import { z } from "zod";
import { Sha256HexSchema } from "./primitives.js";

export const EvalTaskSchema = z.object({
  id: z.string().min(1),
  prompt: z.string().min(1),
  /** Verifiable ground truth; the deterministic scorer compares answers against this. */
  expectedAnswer: z.unknown(),
  /** Optional extra ground-truth context for the LLM judge. */
  groundTruth: z.record(z.unknown()).optional(),
  scorer: z.enum(["exact", "set-equal", "numeric-tol", "llm-judge"]).default("exact")
});
export type EvalTask = z.infer<typeof EvalTaskSchema>;

export const EvalTranscriptStepSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("tool_call"),
    tool: z.string(),
    args: z.unknown()
  }),
  z.object({
    type: z.literal("tool_result"),
    tool: z.string(),
    result: z.unknown(),
    error: z.string().optional()
  }),
  z.object({
    type: z.literal("agent_message"),
    text: z.string()
  })
]);
export type EvalTranscriptStep = z.infer<typeof EvalTranscriptStepSchema>;

export const EvalRunSchema = z.object({
  taskId: z.string(),
  passed: z.boolean(),
  score: z.number().min(0).max(1),
  /** Tool-call transcript. */
  transcript: z.array(EvalTranscriptStepSchema),
  durationMs: z.number().int().nonnegative()
});
export type EvalRun = z.infer<typeof EvalRunSchema>;

export const JudgeCalibrationSchema = z.object({
  cohensKappa: z.number().min(-1).max(1),
  sampleSize: z.number().int().nonnegative(),
  threshold: z.number().min(0).max(1),
  accepted: z.boolean()
});
export type JudgeCalibration = z.infer<typeof JudgeCalibrationSchema>;

/**
 * Agent provenance (Phase-12 honesty contract). A buyer must be able to tell whether
 * a real model or the deterministic-heuristic proxy produced the score.
 *
 *  - `token-overlap`: the offline fallback `TokenOverlapAgent` (cheap, deterministic,
 *    explicitly labelled as a proxy in the buyer-facing card).
 *  - `llm`: the real Claude-backed tool-use loop (`LlmEvalAgent`). `modelId` is the
 *    model identifier (e.g. "claude-opus-4-7"); `fixtureHash` is the canonical sha256
 *    of the recorded transcript bundle used to replay the run (omitted in live mode).
 */
export const EvalAgentInfoSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("token-overlap") }),
  z.object({
    kind: z.literal("llm"),
    modelId: z.string().min(1),
    fixtureHash: z.string().optional()
  })
]);
export type EvalAgentInfo = z.infer<typeof EvalAgentInfoSchema>;

export const EvalReportSchema = z.object({
  /** Bumped to 2 in Phase 12 — `agent` provenance is now required. */
  schemaVersion: z.literal(2).default(2),
  serverDigest: Sha256HexSchema,
  serverKind: z.enum(["designed", "naive-baseline"]),
  tasks: z.array(EvalTaskSchema),
  runs: z.array(EvalRunSchema),
  summary: z.object({
    total: z.number().int().nonnegative(),
    passed: z.number().int().nonnegative(),
    score: z.number().min(0).max(1)
  }),
  /** Required per Phase 12.3 — the buyer reads this to know what produced the score. */
  agent: EvalAgentInfoSchema,
  judgeCalibration: JudgeCalibrationSchema.optional(),
  generatedAt: z.string().datetime({ offset: true })
});
export type EvalReport = z.infer<typeof EvalReportSchema>;
